import {
  FC,
  Fragment,
  useCallback,
  useContext,
  useRef,
  MutableRefObject,
} from "react";
import { Pagination, Table } from "rsuite";
import { useTableSize } from "../../../../hook/width";
import { TableInstance } from "rsuite/Table";
import { TableColumnContext } from "../../../context/TableContext";
import { faCalendarXmark } from "@fortawesome/free-solid-svg-icons";
import { EmptyState } from "../../../components/state/empty";
import { useDataPager } from "../../../../hook/pager";
import { UserGoodsControl } from "../../../../domain/control/Goods";
import { GoodsControlTableTimestampCell } from "./timestamp";
import { GoodsControlTableUnityCell } from "./unity";

import { useStyleContext } from "../../../context/StyleContext";
import { GoodsControlTableColumns } from "../../../../schema/table/GoodsControlTable";
import { GoodsGroupTableActionCell } from "./action";

type Props = {
  goodsControlList?: UserGoodsControl[];
  name: string;
  onSelect: (item: UserGoodsControl) => void;
  onPreviewClicked?: (item: UserGoodsControl) => void;
};

export const GoodsControlTable: FC<Props> = ({
  goodsControlList,
  name,
  onSelect,
  onPreviewClicked,
}) => {
  const actionRef = useRef<string | null>(null) as MutableRefObject<
    string | null
  >;
  const tableRef = useRef<TableInstance<any, any>>(null);
  const tableSize = useTableSize(tableRef);
  const tableContext = useContext(TableColumnContext);
  const tablePreferences =
    tableContext.getTablePreferences<GoodsControlTableColumns>(name);

  const { tableHeaderStyleSecondary } = useStyleContext();

  /* eslint-disable */

  const { partialData, changePage, page, changeLimit, limits, limit } =
    useDataPager({
      id: `goods_table_${name}`,
      items: goodsControlList,
    });

  const onRowSelect = useCallback(
    (item: UserGoodsControl) => {
      if (actionRef.current && onPreviewClicked) onPreviewClicked(item);
      else onSelect(item);
      actionRef.current = null;
    },
    [partialData]
  );
  const onPreviewActionSelected = useCallback(() => {
    actionRef.current = "preview";
  }, [partialData]);
  /* eslint-enable */

  return (
    <Fragment>
      <Table
        className={"rounded md:rounded-xl"}
        autoHeight
        data={partialData}
        ref={tableRef}
        renderEmpty={() => (
          <EmptyState
            icon={faCalendarXmark}
            title={"No hay registros de control de bienes"}
            message={
              "No se encontraron reportes de control de bienes para la fecha seleccionada."
            }
          />
        )}
        onRowClick={onRowSelect as any}
        cellBordered
      >
        {tablePreferences.map((column) => {
          switch (column.name) {
            case "timestamp":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(column.weight)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<GoodsControlTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    {column.label}
                  </Table.HeaderCell>
                  <GoodsControlTableTimestampCell />
                </Table.Column>
              );
            case "unityId":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(column.weight)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<GoodsControlTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    {column.label}
                  </Table.HeaderCell>
                  <GoodsControlTableUnityCell />
                </Table.Column>
              );
            case "provider":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(column.weight)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<GoodsControlTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    {column.label}
                  </Table.HeaderCell>
                  <Table.Cell dataKey={"provider"} />
                </Table.Column>
              );
            case "document":
              return (
                <Table.Column
                  verticalAlign={"middle"}
                  align={"center"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(column.weight)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<GoodsControlTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    {column.label}
                  </Table.HeaderCell>
                  <Table.Cell dataKey={"document"} />
                </Table.Column>
              );
            case "entry":
              return (
                <Table.Column
                  align={"center"}
                  verticalAlign={"middle"}
                  key={`column-${column.name}`}
                  width={tableSize.calculateWeight(column.weight)}
                  onResize={(columnWidth) => {
                    if (columnWidth) {
                      tableContext.updateTableColumnWeight<GoodsControlTableColumns>(
                        name,
                        column.name,
                        tableSize.sizeToWeight(columnWidth)
                      );
                    }
                  }}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    {column.label}
                  </Table.HeaderCell>
                  <Table.Cell dataKey={"entry"} />
                </Table.Column>
              );
            default:
              return (
                <Table.Column
                  align={"center"}
                  verticalAlign={"middle"}
                  width={tableSize.calculateWeight(column.weight)}
                  key={`columns-${column.name}`}
                >
                  <Table.HeaderCell style={tableHeaderStyleSecondary}>
                    Acciones
                  </Table.HeaderCell>
                  <GoodsGroupTableActionCell
                    onPreviewActionClicked={onPreviewActionSelected}
                  />
                </Table.Column>
              );
          }
        })}
      </Table>

      {goodsControlList ? (
        <div className={"py-4 flex flex-col gap-4"}>
          <span className={"text-md"}>
            Mostrando {partialData?.length} elementos / Total{" "}
            {goodsControlList.length}
          </span>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["limit", "|", "pager", "skip"]}
            total={goodsControlList.length}
            limitOptions={limits}
            limit={limit}
            activePage={page}
            onChangePage={changePage}
            onChangeLimit={changeLimit}
          />
        </div>
      ) : null}
    </Fragment>
  );
};
